import React from 'react'
import { UploadCloud, Users, Check, Loader2, X } from 'lucide-react'
import { listContactsCore, getContactCore } from '../lib/contactsCore'
import { supabase } from '../lib/supabase'
import { uploadAudioToStorage, startBackgroundUpload, TRANSCRIBE_STATE_EVENT, firefliesUploadFromStorage, firefliesGetStatus, probeEdgeFunction, type DiarizedSegment } from '../lib/transcribe'
import { getClaimedContactId } from '../lib/profiles'
import { createEvent } from '../lib/events'
import { buildSpeakerLinkMap, embedSpeakerLinks } from '../lib/transcriptSpeakerLink'
import TranscriptBubbles from './TranscriptBubbles'

// In-memory (non-persistent) cache so that soft navigations / component unmounts
// inside the SPA do NOT lose transcription progress. A hard page refresh will
// naturally clear this (desired per requirements).
interface TranscribeMemorySnapshot {
    status: any
    segments: DiarizedSegment[]
    fullText: string
    firefliesSummary: string
    speakerMap: Record<string, string | null>
    error: string | null
    meetingId: string | null
    participants: Array<Participant>
    uploadedPath: string | null
    fileName: string
    lastUpdated: number
}

// Use window global to ensure persistence across module reloads
declare global {
    interface Window {
        __GRISWOLD_TRANSCRIBE_CACHE?: TranscribeMemorySnapshot | null
    }
}

const getMemoryCache = (): TranscribeMemorySnapshot | null => {
    return (window as any).__GRISWOLD_TRANSCRIBE_CACHE || null
}

const setMemoryCache = (snapshot: TranscribeMemorySnapshot | null): void => {
    (window as any).__GRISWOLD_TRANSCRIBE_CACHE = snapshot
}

// Types
type Participant = { contactId?: string; name: string; avatar?: string | null }

interface TranscribePageProps {
    userId?: string
    // view: 'top' renders Meeting Summary + Full Transcript (results) for the main content area
    // view: 'bottom' renders Upload + Participants (controls) for the input/footer area
    view?: 'top' | 'bottom'
}

export default function TranscribePage({ userId, view }: TranscribePageProps) {
    const [file, setFile] = React.useState<File | null>(null)
    const [uploading, setUploading] = React.useState(false)
    const [status, setStatus] = React.useState<'idle' | 'uploading' | 'processing' | 'done' | 'error'>('idle')
    const [error, setError] = React.useState<string | null>(null)
    const [segments, setSegments] = React.useState<DiarizedSegment[]>([])
    const [fullText, setFullText] = React.useState('')
    const [firefliesSummary, setFirefliesSummary] = React.useState('')
    const [whoQuery, setWhoQuery] = React.useState('')
    const [suggestions, setSuggestions] = React.useState<Array<{ id: string; name: string; avatar?: string | null }>>([])
    const [participants, setParticipants] = React.useState<Array<Participant>>([])
    const [myContactId, setMyContactId] = React.useState<string | null>(null)
    const [speakerMap, setSpeakerMap] = React.useState<Record<string, string | null>>({})
    const fileInputRef = React.useRef<HTMLInputElement | null>(null)
    const [dragActive, setDragActive] = React.useState(false)
    const [uploadedPath, setUploadedPath] = React.useState<string | null>(null)
    const [fileName, setFileName] = React.useState<string>('')
    const [meetingId, setMeetingId] = React.useState<string | null>(null)
    const pollingRef = React.useRef(false)
    const [showSuccessModal, setShowSuccessModal] = React.useState(false)
    const [savedContactIds, setSavedContactIds] = React.useState<string[]>([])
    const [savedContactNames, setSavedContactNames] = React.useState<string[]>([])
    const cancelRef = React.useRef(false)
    const [myContactName, setMyContactName] = React.useState<string>('You')

    // Helper to get proper display name for user's contact
    const getMyContactDisplayName = React.useCallback(async (contactId: string): Promise<string> => {
        try {
            const c: any = await getContactCore(supabase as any, contactId)
            const display = `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || c?.email || 'You'
            console.log('🔍 Loading contact display name:', { contactId, firstName: c?.first_name, lastName: c?.last_name, email: c?.email, display })
            setMyContactName(display)
            return display
        } catch (e) {
            console.warn('⚠️ Failed to load contact display name:', e)
            setMyContactName('You')
            return 'You'
        }
    }, [])

    // Helper to reset participants with proper contact name
    const resetParticipantsWithMyContact = React.useCallback(async () => {
        if (myContactId) {
            try {
                const display = await getMyContactDisplayName(myContactId)
                const c: any = await getContactCore(supabase as any, myContactId)
                const avatar = c?.profile_image_url || c?.photo || null
                setParticipants([{ contactId: myContactId, name: display, avatar }])
            } catch {
                setParticipants([{ contactId: myContactId, name: myContactName, avatar: null }])
            }
        } else {
            setParticipants([])
        }
    }, [myContactId, myContactName, getMyContactDisplayName])

    // Hydrate from in-memory cache first (soft navigation resume). Fallback to sessionStorage.
    React.useEffect(() => {
        let hydrated = false
        console.log('[Transcribe] Attempting to hydrate state...')

        try {
            const memorySnapshot = getMemoryCache()
            if (memorySnapshot) {
                console.log('[Transcribe] Found memory cache, restoring state:', memorySnapshot.status, memorySnapshot.meetingId)
                const s = memorySnapshot
                setStatus(s.status)
                setSegments(s.segments)
                setFullText(s.fullText)
                setFirefliesSummary(s.firefliesSummary)
                setSpeakerMap(s.speakerMap)
                setError(s.error)
                setMeetingId(s.meetingId)
                setParticipants(s.participants)
                setUploadedPath(s.uploadedPath)
                setFileName(s.fileName)
                hydrated = true
            } else {
                console.log('[Transcribe] No memory cache found')
            }
        } catch (e) {
            console.warn('[Transcribe] Error reading memory cache:', e)
        }

        if (!hydrated) {
            try {
                const saved = sessionStorage.getItem('transcribe-state')
                if (saved) {
                    console.log('[Transcribe] Falling back to sessionStorage')
                    const state = JSON.parse(saved)
                    if (state.status) setStatus(state.status)
                    if (state.segments) setSegments(state.segments)
                    if (state.fullText) setFullText(state.fullText)
                    if (state.firefliesSummary) setFirefliesSummary(state.firefliesSummary)
                    if (state.speakerMap) setSpeakerMap(state.speakerMap)
                    if (state.error) setError(state.error)
                    if (state.meetingId) setMeetingId(state.meetingId)
                    if (state.uploadedPath) setUploadedPath(state.uploadedPath)
                    if (state.fileName) setFileName(state.fileName)
                    if (Array.isArray(state.participants)) setParticipants(state.participants)
                } else {
                    console.log('[Transcribe] No saved state found anywhere')
                }
            } catch (e) {
                console.warn('[Transcribe] Error reading sessionStorage:', e)
            }
        }
    }, [])

    // Save state changes (sessionStorage + in-memory snapshot)
    React.useEffect(() => {
        const snapshot: TranscribeMemorySnapshot = {
            status, segments, fullText, firefliesSummary, speakerMap, error, meetingId, participants, uploadedPath, fileName,
            lastUpdated: Date.now()
        }
        console.log('[Transcribe] Saving state:', status, meetingId, 'segments:', segments.length)
        setMemoryCache(snapshot)
        try { sessionStorage.setItem('transcribe-state', JSON.stringify(snapshot)) } catch { /* ignore */ }
    }, [status, segments, fullText, firefliesSummary, speakerMap, error, meetingId, participants, uploadedPath, fileName])

    // Clear in-memory cache along with sessionStorage
    const clearPersistedState = React.useCallback(() => {
        console.log('[Transcribe] Clearing all persisted state')
        setMemoryCache(null)
        try { sessionStorage.removeItem('transcribe-state') } catch { }
    }, [])

    // Prefill "Me" & legacy conditional resume (kept, but auto-resume effect below now handles generic case)
    React.useEffect(() => {
        (async () => {
            if (!userId) { setParticipants([]); setMyContactId(null); return }
            try {
                const myId = await getClaimedContactId(userId)
                setMyContactId(myId)
                if (myId) {
                    try {
                        const display = await getMyContactDisplayName(myId)
                        const c: any = await getContactCore(supabase as any, myId)
                        const avatar = c?.profile_image_url || c?.photo || null
                        setParticipants(prev => prev.length ? prev : [{ contactId: myId, name: display, avatar }])
                    } catch { }
                }
            } catch { }
            // Resume polling if a meeting was in-flight
            try {
                const saved = JSON.parse(sessionStorage.getItem('transcribe-state') || '{}')
                const savedMeeting = saved.meetingId as string | null
                const savedStatus = saved.status as string | null
                const stagedUpload = !!saved.uploadedPath && !saved.meetingId
                if (savedMeeting && !pollingRef.current && savedStatus === 'processing' && !stagedUpload) {
                    pollingRef.current = true
                    setStatus('processing')
                    await pollStatus(savedMeeting)
                    pollingRef.current = false
                }
            } catch { /* ignore */ }
        })()
    }, [userId])

    // Generic auto-resume: if we mount with a processing meeting and polling isn't running, restart it.
    React.useEffect(() => {
        if (status === 'processing' && meetingId && !pollingRef.current) {
            console.log('[Transcribe] Auto-resuming polling for meeting:', meetingId)
            pollingRef.current = true
                ; (async () => { try { await pollStatus(meetingId) } finally { pollingRef.current = false } })()
        }
    }, [status, meetingId])

    // Save state on unmount to ensure persistence
    React.useEffect(() => {
        return () => {
            console.log('[Transcribe] Component unmounting, saving final state')
            const snapshot: TranscribeMemorySnapshot = {
                status, segments, fullText, firefliesSummary, speakerMap, error, meetingId, participants, uploadedPath, fileName,
                lastUpdated: Date.now()
            }
            setMemoryCache(snapshot)
            try { sessionStorage.setItem('transcribe-state', JSON.stringify(snapshot)) } catch { /* ignore */ }
        }
    }, [status, segments, fullText, firefliesSummary, speakerMap, error, meetingId, participants, uploadedPath, fileName])

    // Listen for background upload state (avoid status changes to prevent flicker)
    React.useEffect(() => {
        const onState = (e: any) => {
            const d = e?.detail || {}
            if (typeof d.uploading === 'boolean') setUploading(!!d.uploading)
            if (typeof d.uploadedPath === 'string') setUploadedPath(d.uploadedPath)
            if (typeof d.fileName === 'string') setFileName(d.fileName)
            if (typeof d.error === 'string') setError(d.error)
        }
        window.addEventListener(TRANSCRIBE_STATE_EVENT, onState as any)
        try {
            const up = sessionStorage.getItem('transcribe:uploadedPath')
            const st = sessionStorage.getItem('transcribe:status') as any
            const fn = sessionStorage.getItem('transcribe:fileName') || ''
            if (up) setUploadedPath(up)
            if (fn) setFileName(fn)
            if (st === 'uploading') setUploading(true)
        } catch { }
        return () => window.removeEventListener(TRANSCRIBE_STATE_EVENT, onState as any)
    }, [])

    // Suggestions
    React.useEffect(() => {
        const q = whoQuery.trim(); if (!q) { setSuggestions([]); return }
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: q, limit: 8 } as any)
                setSuggestions((rows || []).map((c: any) => ({ id: c.id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), avatar: c.profile_image_url || null })))
            } catch { setSuggestions([]) }
        }, 250)
        return () => clearTimeout(t)
    }, [whoQuery])

    async function startTranscribe() {
        cancelRef.current = false
        setError(null)
        // Prefer using an already uploaded path (selected from top region)
        if (uploadedPath) {
            try {
                setStatus('processing')
                const title = (fileName || 'Transcript').replace(/\.[^.]+$/, '')
                const attendees = participants.map(p => ({ name: p.name }))
                const res: any = await firefliesUploadFromStorage({ storagePath: uploadedPath, title, attendees })
                setMeetingId(res?.meeting_id || null)
                await pollStatus(res.meeting_id)
            } catch (e: any) {
                setError(e?.message || 'Failed to start transcription')
                setStatus('error')
            }
            return
        }
        if (!file) return
        setUploading(true); setStatus('uploading'); setError(null)
        try {
            try { await probeEdgeFunction() } catch { }
            const { path } = await uploadAudioToStorage(file, 'transcripts')
            setUploadedPath(path)
            const title = file.name.replace(/\.[^.]+$/, '')
            setFileName(file.name)
            const attendees = participants.map(p => ({ name: p.name }))
            const res: any = await firefliesUploadFromStorage({ storagePath: path, title, attendees })
            setMeetingId(res?.meeting_id || null)
            setStatus('processing')
            await pollStatus(res.meeting_id)
        } catch (e: any) {
            setError(e?.message || 'Failed to start transcription')
            setStatus('error')
        } finally { setUploading(false) }
    }

    async function pollStatus(id: string) {
        const pollStartTime = Date.now()
        const timeoutMs = 1000 * 60 * 10
        let webhookTranscriptId: string | null = null
        while (Date.now() - pollStartTime < timeoutMs) {
            if (cancelRef.current) {
                // User cancelled – revert to idle state and stop polling
                setStatus('idle')
                setMeetingId(null)
                setSegments([])
                setFullText('')
                setFirefliesSummary('')
                setSpeakerMap({})
                setError(null)
                return
            }
            await delay(4000)
            try {
                const { data: webhookData, error: webhookError } = await supabase
                    .from('fireflies_transcripts')
                    .select('status, transcript_id, meeting_id, payload')
                    .eq('client_reference_id', id)
                    .maybeSingle()
                if (!webhookError && webhookData) {
                    const webhookStatus = webhookData.status || webhookData.payload?.eventType
                    if (webhookStatus && (/completed|done|ready/i.test(webhookStatus) || webhookStatus.includes('completed'))) {
                        webhookTranscriptId = webhookData.transcript_id || webhookData.meeting_id
                    }
                }
                const idToUse = webhookTranscriptId || id
                const res = await firefliesGetStatus(idToUse)
                const segs = res.segments || []
                const hasAnySegments = segs.length > 0 && segs.some((s: any) => s.speaker && s.text?.trim())
                if ((res.status && /completed|done|ready/i.test(res.status)) && hasAnySegments) {
                    setStatus('done')
                    setSegments(segs)
                    setFullText(res.text || '')
                    setFirefliesSummary(res.summary || '')
                    try {
                        const labels = Array.from(new Set(segs.map((s: any) => s.speaker).filter(Boolean)))
                        const init: Record<string, string | null> = {}
                        labels.forEach(l => { init[l] = null })
                        setSpeakerMap(init)
                    } catch { }
                    try { if (uploadedPath) { await supabase.storage.from('audio').remove([uploadedPath]) } } catch { }
                    setUploadedPath(null)
                    return
                }
            } catch (e: any) { setError(e?.message || 'Status check failed'); break }
        }
        setError('Transcription timeout - speaker diarization may still be processing. You can try refreshing or retry later.'); setStatus('error')
        try { if (uploadedPath) { await supabase.storage.from('audio').remove([uploadedPath]) } } catch { }
        setUploadedPath(null)
    }

    // Cancel current in-flight work and return to pre-start state
    function cancelTranscription() {
        cancelRef.current = true
        // If we were uploading via foreground path, make sure uploading flag clears
        setUploading(false)
        // We intentionally keep uploadedPath and fileName so the user can Start again without re-uploading
    }

    // When a file is chosen in the top view, upload it immediately so the bottom Start button can use the shared uploadedPath
    const handleFileChosen = async (f: File) => {
        if (!f) return
        setFileName(f.name)
        // Reset any prior run so a previous meeting doesn't auto-resume
        setMeetingId(null)
        setSegments([])
        setFullText('')
        setFirefliesSummary('')
        setSpeakerMap({})
        setUploading(true)
        setError(null)
        try {
            try { await probeEdgeFunction() } catch { }
            // Use background upload so navigation doesn't cancel progress
            const { path } = await startBackgroundUpload(f, 'transcripts')
            setUploadedPath(path)
            // Do not auto-start; require user to click Start
            setStatus('idle')
        } catch (e: any) {
            setError(e?.message || 'Failed to upload file')
            setStatus('error')
        } finally {
            setUploading(false)
        }
    }

    async function saveAsEvent() {
        try {
            // CORRECT LOGIC: Only save participants that were ASSIGNED to speakers
            // Selecting participants is just staging - assignment to speakers is what counts
            const assignedNames = new Set<string>(Object.values(speakerMap || {}).filter((v): v is string => !!v));
            console.log('SaveAsEvent: Assigned speaker names:', Array.from(assignedNames));

            // Only include participants who were assigned to speakers
            let normalizedParticipants = participants.filter(p => assignedNames.has(p.name));
            console.log('SaveAsEvent: Only assigned participants will be saved:', normalizedParticipants);
            console.log('SaveAsEvent: Speaker assignments:', speakerMap);
            console.log('SaveAsEvent: Participants structure:', JSON.stringify(normalizedParticipants, null, 2));

            const text = buildAssignedText(segments, fullText, speakerMap)
            // Collect all participant contactIds (including self when available)
            const ids = Array.from(new Set([
                ...(myContactId ? [myContactId] : []),
                ...normalizedParticipants.map(p => p.contactId).filter((v): v is string => !!v)
            ]))
            if (ids.length === 0) throw new Error('Select at least one participant who is a saved contact to attach the event to.')
            // Build and embed deterministic speaker/contact links (throws on failure)
            const mapping = buildSpeakerLinkMap(normalizedParticipants, speakerMap)
            const notesWithMapping = embedSpeakerLinks(text, mapping)
            // Build chat payload for timeline rendering (stable across viewers)
            const chatSegments = (segments || []).map((s) => {
                const assigned = (speakerMap && s.speaker in speakerMap && speakerMap[s.speaker]) ? (speakerMap[s.speaker] as string) : s.speaker
                return { name: assigned || s.speaker, text: s.text }
            })
            const chatPayload: any = {
                primary: (normalizedParticipants && normalizedParticipants[0]?.name) || null,
                participants: normalizedParticipants,
                segments: chatSegments,
            }
            // Use Fireflies meeting id when available, else generate a deterministic shared id
            const sharedTranscriptId = meetingId || (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
            
            // Ensure name is never null or empty
            const eventName = fileName ? `${fileName} Transcript` : 'Call Transcript'
            
            const payload: any = {
                name: eventName, // Guaranteed non-null
                notes: notesWithMapping,
                participants: normalizedParticipants,
                entries: [],
                when: new Date().toISOString(), // Add current date/time
                customFields: {
                    eventType: 'Transcribed',
                    chat: chatPayload,
                    transcriptText: text,
                    sharedTranscriptId,
                }
            }
            console.log('SaveAsEvent: Complete payload being saved:', JSON.stringify(payload, null, 2));
            const successfulSaves = [];
            const failedSaves = [];
            
            for (const cid of ids) {
                console.log('SaveAsEvent: Creating event for contact:', cid);
                console.log('SaveAsEvent: Payload being sent:', { name: payload.name, contactId: cid });
                try {
                    const savedEvent = await createEvent(cid, payload);
                    console.log('SaveAsEvent: Event created successfully:', savedEvent);
                    successfulSaves.push(cid);
                } catch (e) {
                    console.error(`Failed to create transcript event for contact ${cid}:`, e);
                    failedSaves.push({ contactId: cid, error: e });
                }
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId: cid } })) } catch { }
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshEvents', { detail: { contactId: cid } })) } catch { }
            }
            
            if (failedSaves.length > 0) {
                console.warn('Some transcript events failed to save:', failedSaves);
                setError(`Failed to save transcript for ${failedSaves.length} contact(s). Check console for details.`);
            }
            // Collect contact information for the success modal (only successful saves)
            if (successfulSaves.length > 0) {
                const contactsWithIds = participants.filter(p => p.contactId && successfulSaves.includes(p.contactId))
                setSavedContactIds(successfulSaves)
                setSavedContactNames(contactsWithIds.map(p => p.name))
                setShowSuccessModal(true)
            }

            // Only clear state if all saves were successful
            if (failedSaves.length === 0) {
                setStatus('idle'); setSegments([]); setFullText(''); setFirefliesSummary(''); setSpeakerMap({}); setFile(null); 
                // Reset with proper contact name, not "Me"
                resetParticipantsWithMyContact()
                clearPersistedState()
            }
        } catch (e: any) { setError(e?.message || 'Failed to save event') }
    }

    return (
        <div className="p-4 lg:p-6">
            <div className="max-w-5xl mx-auto">
                {view === 'top' && (
                    <>
                        {(status === 'idle' || status === 'error') && (
                            <div className="flex-1 flex flex-col items-center justify-start">
                                <div className="w-full max-w-2xl">
                                    <div
                                        className={`relative rounded-2xl border-2 border-dashed ${dragActive ? 'border-blue-500 bg-blue-500/10' : 'border-gray-600 bg-gray-900/50'} p-12 flex flex-col items-center justify-center text-center cursor-pointer hover:border-gray-500 hover:bg-gray-800/50 transition-all duration-200 mb-6`}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
                                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
                                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith('audio/')) handleFileChosen(f) }}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <div className="w-20 h-20 rounded-full bg-gray-800 border border-gray-700 grid place-items-center mb-3">
                                            {uploading ? <Loader2 className="w-10 h-10 text-blue-400 animate-spin" /> : <UploadCloud className="w-10 h-10 text-gray-300" />}
                                        </div>
                                        {uploading && <div className="mb-3 text-sm text-gray-300">Uploading…</div>}
                                        <h3 className="text-2xl font-light text-white mb-2">{fileName ? fileName : 'Drag your audio file here'}</h3>
                                        <p className="text-gray-400 mb-6">or click to browse • mp3, m4a, wav, ogg, webm</p>
                                        <input ref={fileInputRef} type="file" accept="audio/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChosen(f) }} className="absolute inset-0 opacity-0 pointer-events-none" tabIndex={-1} />
                                        {uploadedPath && !uploading && status === 'idle' && (
                                            <div className="mt-2 text-xs text-gray-400">Ready to start transcription below.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {status === 'done' && (
                            <>
                                <div className="w-full bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                                    <h3 className="text-xl font-medium text-white mb-4">Meeting Summary</h3>
                                    <div className="text-base text-gray-200 whitespace-pre-wrap leading-relaxed">{firefliesSummary || 'No summary available'}</div>
                                </div>
                                <div className="w-full bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-medium text-white">Full Transcript</h3>
                                        <button
                                            onClick={() => {
                                                // Allow re-editing by changing status back to 'done' but keeping all data
                                                setStatus('done');
                                            }}
                                            className="px-3 py-1 text-xs border border-gray-600 text-gray-300 hover:bg-gray-700 rounded"
                                            title="Modify speaker assignments and re-save"
                                        >
                                            Edit & Re-save
                                        </button>
                                    </div>
                                    {(() => { try { console.debug('[Transcribe] Render transcript bubbles', { segs: segments.length, participants }); } catch { } return null; })()}
                                    <TranscriptBubbles segments={segments as any} map={speakerMap as any} participants={participants as any} />
                                    <div className="mt-6 flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2"><AiAsk fullText={fullText} segments={segments} /><AiWriteup fullText={fullText} segments={segments} participants={participants} /></div>
                                    </div>
                                </div>
                            </>
                        )}
                        {status === 'processing' && (
                            <div className="mb-4 bg-gray-900 border border-gray-800 rounded-xl p-4 text-gray-300">
                                <div className="flex items-center gap-3"><Loader2 className="w-4 h-4 animate-spin text-blue-400" />Processing your audio…</div>
                                <div className="text-xs text-gray-400 mt-2">You can still choose participants below while this runs.</div>
                            </div>
                        )}
                    </>
                )}

                {!view && (
                    <>
                        {/* Main content with extra bottom space so fixed footer doesn't cover */}
                        <div className="pb-[340px]">
                            {(status === 'idle' || status === 'error') && (
                                <div className="flex-1 flex flex-col items-center justify-start">
                                    <div className="w-full max-w-2xl">
                                        <div
                                            className={`relative rounded-2xl border-2 border-dashed ${dragActive ? 'border-blue-500 bg-blue-500/10' : 'border-gray-600 bg-gray-900/50'} p-12 flex flex-col items-center justify-center text-center cursor-pointer hover:border-gray-500 hover:bg-gray-800/50 transition-all duration-200 mb-6`}
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
                                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
                                            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith('audio/')) handleFileChosen(f) }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <div className="w-20 h-20 rounded-full bg-gray-800 border border-gray-700 grid place-items-center mb-3">
                                                {uploading ? <Loader2 className="w-10 h-10 text-blue-400 animate-spin" /> : <UploadCloud className="w-10 h-10 text-gray-300" />}
                                            </div>
                                            {uploading && <div className="mb-3 text-sm text-gray-300">Uploading…</div>}
                                            <h3 className="text-2xl font-light text-white mb-2">{fileName ? fileName : 'Drag your audio file here'}</h3>
                                            <p className="text-gray-400 mb-6">or click to browse • mp3, m4a, wav, ogg, webm</p>
                                            <input ref={fileInputRef} type="file" accept="audio/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChosen(f) }} className="absolute inset-0 opacity-0 pointer-events-none" tabIndex={-1} />
                                            {uploadedPath && !uploading && status === 'idle' && (
                                                <div className="mt-2 text-xs text-gray-400">Ready to start transcription below.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {status === 'processing' && (
                                <div className="mb-4 bg-gray-900 border border-gray-800 rounded-xl p-4 text-gray-300">
                                    <div className="flex items-center gap-3"><Loader2 className="w-4 h-4 animate-spin text-blue-400" />Processing your audio…</div>
                                    <div className="text-xs text-gray-400 mt-2">You can still choose participants below while this runs.</div>
                                </div>
                            )}
                            {status === 'done' && (
                                <>
                                    <div className="w-full bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                                        <h3 className="text-xl font-medium text-white mb-4">Meeting Summary</h3>
                                        <div className="text-base text-gray-200 whitespace-pre-wrap leading-relaxed">{firefliesSummary || 'No summary available'}</div>
                                    </div>
                                    <div className="w-full bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
                                        <h3 className="text-xl font-medium text-white mb-4">Full Transcript</h3>
                                        {(() => { try { console.debug('[Transcribe] Render transcript bubbles', { segs: segments.length, participants }); } catch { } return null; })()}
                                        <TranscriptBubbles segments={segments as any} map={speakerMap as any} participants={participants as any} />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Compact integrated footer (shifts with sidebar via CSS var) */}
                        <div className="fixed bottom-0 right-0 z-40 border-t border-white/10 bg-black/95 backdrop-blur-md" style={{ left: 'var(--gv-sidebar-width,0px)' }}>
                            <div className="w-full pb-[env(safe-area-inset-bottom)] pt-2 flex flex-col max-w-5xl mx-auto px-2 sm:px-5">
                                {/* Primary actions row */}
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    {status === 'idle' && (
                                        <button onClick={() => { startTranscribe() }} disabled={uploading || !uploadedPath} className="order-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-blue-700 bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full sm:w-auto">
                                            <UploadCloud className="w-4 h-4" /> Start Transcription
                                        </button>
                                    )}
                                    {status === 'processing' && (
                                        <div className="order-1 flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-xs sm:text-sm text-gray-200 w-full sm:w-auto">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" /> Processing audio…
                                        </div>
                                    )}
                                    <div className="order-2 flex items-center gap-1 ml-auto">
                                        {status === 'done' && Object.keys(speakerMap).length > 0 && (
                                            <button 
                                                onClick={saveAsEvent} 
                                                disabled={!fileName && !segments.length} 
                                                title="Save to events" 
                                                className="p-2 rounded-md border border-gray-700 text-green-400 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                        {status !== 'idle' && (
                                            <button onClick={() => { if (status === 'processing') { cancelTranscription(); } else { setStatus('idle'); setSegments([]); setFullText(''); setSpeakerMap({}); setFile(null); setError(null); resetParticipantsWithMyContact(); clearPersistedState(); } }} title="Cancel" className="p-2 rounded-md border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"><X className="w-4 h-4" /></button>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-gray-900/95 border border-gray-800 rounded-none sm:rounded-xl sm:mx-0 -mx-2 sm:p-4 p-3 flex flex-col gap-2 w-[calc(100%+1rem)] sm:w-full self-center sm:self-auto">
                                    <div className="flex items-center justify-between gap-2">
                                        <h3 className="text-sm sm:text-base font-medium text-white">{status === 'done' ? 'Assign & Participants' : 'Participants'}</h3>
                                    </div>
                                    {status === 'processing' && (
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-xs sm:text-sm text-gray-200">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" /> Processing audio… add participants below.
                                        </div>
                                    )}
                                    {status === 'done' && Object.keys(speakerMap).length > 0 && (
                                        <div className="flex flex-wrap gap-3 mb-4">
                                            {Object.keys(speakerMap).map(sp => (
                                                <div key={sp} className="flex flex-col">
                                                    <label className="text-xs text-gray-400 mb-1">Assign {sp}:</label>
                                                    <select
                                                        value={speakerMap[sp] || ''}
                                                        onChange={async (e) => {
                                                            const selectedName = e.target.value || null;
                                                            setSpeakerMap(prev => ({ ...prev, [sp]: selectedName }));

                                                            // Update participants array with contact information
                                                            if (selectedName) {
                                                                try {
                                                                    // Search for the contact by name to get full details
                                                                    const contacts = await listContactsCore(supabase as any, { query: selectedName, limit: 5 });
                                                                    const matchedContact = contacts?.find(c =>
                                                                        `${c.first_name || ''} ${c.last_name || ''}`.trim() === selectedName
                                                                    );

                                                                    if (matchedContact) {
                                                                        // Add or update participant with contact details
                                                                        setParticipants(prev => {
                                                                            const existingIndex = prev.findIndex(p => p.name === selectedName);
                                                                            const newParticipant = {
                                                                                contactId: matchedContact.id,
                                                                                name: selectedName,
                                                                                avatar: matchedContact.profile_image_url || null
                                                                            };

                                                                            if (existingIndex >= 0) {
                                                                                // Update existing participant
                                                                                const updated = [...prev];
                                                                                updated[existingIndex] = newParticipant;
                                                                                return updated;
                                                                            } else {
                                                                                // Add new participant
                                                                                return [...prev, newParticipant];
                                                                            }
                                                                        });
                                                                    }
                                                                } catch (error) {
                                                                    console.error('Failed to fetch contact details:', error);
                                                                }
                                                            }
                                                        }}
                                                        className="appearance-none px-4 py-2 rounded-lg border border-gray-600 bg-gray-800 text-white text-sm min-w-[140px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                                                    >
                                                        <option value="" className="bg-gray-800 text-white">{sp} (Unassigned)</option>
                                                        {participants.map((p, pIdx) => (
                                                            <option key={(p.contactId || p.name) + pIdx} value={p.name} className="bg-gray-800 text-white">{p.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="relative">
                                        <Users className="hidden sm:block w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        <input value={whoQuery} onChange={(e) => setWhoQuery(e.target.value)} placeholder="Search or Enter name" className="w-full pl-3 sm:pl-9 pr-2 sm:pr-3 py-2.5 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 text-sm" onKeyDown={(e) => { if (e.key === 'Enter') { const nm = whoQuery.trim(); if (nm) { setParticipants(prev => [...prev, { name: nm }]); setWhoQuery('') } } }} />
                                    </div>
                                    {suggestions.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 overflow-auto pr-1" style={{ maxHeight: participants.length ? '70px' : '110px' }}>
                                            {suggestions.map(s => (
                                                <button key={s.id} type="button" onClick={() => { setParticipants(prev => prev.some(p => p.contactId === s.id) ? prev : [...prev, { contactId: s.id, name: s.name, avatar: s.avatar || null }]); setWhoQuery('') }} className="flex items-center gap-3 p-2.5 rounded-md border border-gray-700 hover:bg-gray-700 text-left">
                                                    <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0">{s.avatar ? <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" /> : null}</div>
                                                    <div className="min-w-0"><div className="text-sm text-gray-200 truncate">{s.name}</div></div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {participants.length > 0 && (
                                        <div className="flex flex-wrap gap-2 overflow-auto pr-1" style={{ maxHeight: suggestions.length ? '60px' : '90px' }}>
                                            {participants.map((p, idx) => (
                                                <span key={(p.contactId || p.name) + idx} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-900 border border-gray-700 text-gray-200 text-sm">
                                                    {p.avatar ? <img src={p.avatar} className="w-5 h-5 rounded-full" /> : null}
                                                    <span className="truncate max-w-[40vw] sm:max-w-none">{p.name}</span>
                                                    <button className="ml-1 text-gray-400 hover:text-white w-6 h-6 flex items-center justify-center" onClick={() => setParticipants(prev => prev.filter((_, i) => i !== idx))} aria-label={`Remove ${p.name}`}>×</button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {error && <div className="text-center text-red-300 text-xs">{error}</div>}
                                    {status === 'done' && Object.keys(speakerMap).length > 0 && (!fileName && !segments.length) && (
                                        <div className="text-center text-yellow-300 text-xs">
                                            ⚠️ No transcript content available to save
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="relative w-full max-w-md bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-2xl">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">
                                {error ? 'Transcript Partially Saved' : 'Transcript Saved!'}
                            </h3>
                            <p className="text-gray-400">
                                Successfully saved to {savedContactIds.length} contact{savedContactIds.length > 1 ? 's' : ''}
                                {error && <span className="block text-red-400 text-sm mt-1">{error}</span>}
                            </p>
                        </div>

                        {savedContactNames.length > 0 && (
                            <div className="mb-6">
                                <p className="text-sm font-medium text-gray-300 mb-3">Saved to:</p>
                                <div className="space-y-2">
                                    {savedContactNames.map((name, index) => (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                const contactId = savedContactIds[index];
                                                if (contactId) {
                                                    console.log('🔍 Navigating to contact:', contactId);
                                                    // Use the proper navigation event that the app listens for
                                                    window.dispatchEvent(new CustomEvent('gv:openContact', {
                                                        detail: { contactId }
                                                    }));
                                                }
                                                setShowSuccessModal(false);
                                            }}
                                            className="w-full flex items-center justify-between p-3 bg-gray-800 border border-gray-700 rounded-lg text-left hover:bg-gray-750 transition-colors"
                                        >
                                            <span className="text-white font-medium">{name}</span>
                                            <span className="text-blue-400 text-sm">Go to Contact →</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function delay(ms: number) { return new Promise(res => setTimeout(res, ms)) }

function speakerDisplay(nameOrLabel: string, map?: Record<string, string | null>): string {
    if (!map) return nameOrLabel
    const mapped = map[nameOrLabel]
    return (mapped || nameOrLabel)
}

// Helper to display GraphQL type names from introspection
function typeName(t: any): string {
    if (!t) return 'unknown'
    // Unwrap nested ofType
    let cur = t
    const parts: string[] = []
    while (cur) {
        if (cur.kind === 'NON_NULL') parts.push('!')
        if (cur.name) parts.unshift(cur.name)
        cur = cur.ofType || null
        if (!cur) break
    }
    return parts.join('') || 'unknown'
}

function buildAssignedText(segments: DiarizedSegment[], fallback: string, map?: Record<string, string | null>): string {
    if (segments && segments.length > 0) {
        return segments.map(s => {
            const label = (map && s.speaker in map && map[s.speaker]) ? (map[s.speaker] as string) : s.speaker
            return `${label}: ${s.text}`.trim()
        }).join('\n')
    }
    return fallback || ''
}

// speakerDisplay retained for assigned text construction

function AiAsk({ fullText, segments }: { fullText: string; segments: DiarizedSegment[] }) {
    const [open, setOpen] = React.useState(false)
    const [question, setQuestion] = React.useState('')
    const [answer, setAnswer] = React.useState<string | null>(null)
    const [loading, setLoading] = React.useState(false)
    const ask = async () => {
        if (!question.trim()) return
        setLoading(true); setAnswer(null)
        try {
            const prompt = `You are helping analyze a call transcript. Answer the question concisely using only the information from the transcript.\n\nTranscript:\n${fullText || segments.map(s => `${s.speaker}: ${s.text}`).join('\n')}\n\nQuestion: ${question}`
            const { data, error } = await supabase.functions.invoke('gemini-ai', { body: { prompt } })
            if (error) throw error
            setAnswer((data as any)?.text || 'No answer')
        } catch (e: any) {
            setAnswer(e?.message || 'Failed to get answer')
        } finally { setLoading(false) }
    }
    return (
        <div className="relative">
            <button onClick={() => setOpen(v => !v)} className="px-2.5 py-1.5 text-xs rounded-md border border-gray-700 bg-gray-900 text-gray-100">Ask AI</button>
            {open && (
                <div className="absolute z-10 mt-2 w-80 p-3 bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                    <div className="text-xs text-gray-400 mb-1">Ask about this conversation</div>
                    <div className="flex items-center gap-2">
                        <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="e.g., What did they agree on?" className="flex-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-gray-200 text-xs" />
                        <button onClick={ask} disabled={loading || !question.trim()} className="px-2 py-1 text-xs rounded bg-gray-800 border border-gray-700 text-white disabled:opacity-50">{loading ? 'Asking…' : 'Ask'}</button>
                    </div>
                    {answer && <div className="mt-2 text-xs text-gray-200 whitespace-pre-wrap">{answer}</div>}
                </div>
            )}
        </div>
    )
}

function AiWriteup({ fullText, segments, participants }: { fullText: string; segments: DiarizedSegment[]; participants: Participant[] }) {
    const [open, setOpen] = React.useState(false)
    const [result, setResult] = React.useState<string | null>(null)
    const [loading, setLoading] = React.useState(false)
    const generate = async () => {
        setLoading(true); setResult(null)
        try {
            const who = participants.map(p => p.name).join(', ')
            const prompt = `Write a crisp, executive-style summary of the following conversation. Include participants (${who}) and key takeaways with bullets.\n\nTranscript:\n${fullText || segments.map(s => `${s.speaker}: ${s.text}`).join('\n')}`
            const { data, error } = await supabase.functions.invoke('gemini-ai', { body: { prompt } })
            if (error) throw error
            setResult((data as any)?.text || 'No summary')
        } catch (e: any) {
            setResult(e?.message || 'Failed to generate summary')
        } finally { setLoading(false) }
    }
    return (
        <div className="relative">
            <button onClick={() => setOpen(v => !v)} className="px-2.5 py-1.5 text-xs rounded-md border border-gray-700 bg-gray-900 text-gray-100">Generate write‑up</button>
            {open && (
                <div className="absolute z-10 mt-2 w-[30rem] max-w-[80vw] p-3 bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                    <div className="flex items-center gap-2 mb-2">
                        <button onClick={generate} disabled={loading} className="px-2 py-1 text-xs rounded bg-gray-800 border border-gray-700 text-white disabled:opacity-50">{loading ? 'Generating…' : 'Generate'}</button>
                        {result && <button onClick={() => { try { navigator.clipboard.writeText(result) } catch { } }} className="px-2 py-1 text-xs rounded border border-gray-700 text-gray-200">Copy</button>}
                    </div>
                    {result && <div className="text-xs text-gray-200 whitespace-pre-wrap max-h-72 overflow-auto">{result}</div>}
                </div>
            )}
        </div>
    )
}

// Backward-compatible modal wrapper for legacy usages
// Note: This renders the TranscribePage inside a full-screen overlay when open=true
export function TranscribeSaveEventModal({ open, onClose, userId }: { open: boolean; onClose?: () => void; userId?: string }) {
    if (!open) return null
    return (
        <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-auto rounded-xl border border-gray-800 bg-black shadow-2xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-400 hover:text-white text-sm border border-gray-700 rounded-md px-2 py-1 bg-gray-900"
                >Close</button>
                <TranscribePage userId={userId} />
            </div>
        </div>
    )
}

